import type { ColumnSizingState } from '@tanstack/react-table';

import { DEFAULT_COLUMN_SIZE } from '../constants';
import type { ColumnConfig } from '../types';

/**
 * Calculates enhanced column sizes to fill container width.
 * Ported from data-table-old/use-data-table.ts calculateEnhancedSizes.
 *
 * Key behaviors:
 * - If a column has growFirst=true, that column grows to fill extra space
 * - If no growFirst is specified, extra space is distributed equally across all columns
 * - Returns only enhanced sizes (not a complete map) to allow TanStack defaults to work
 * - Respects user-resized columns by using currentSizes as base
 */
export function calculateEnhancedSizes<TData>(
  columns: ColumnConfig<TData>[],
  containerWidth: number,
  currentSizes: ColumnSizingState,
  defaultColumnSize: number = DEFAULT_COLUMN_SIZE
): ColumnSizingState {
  // If container not measured yet, return current sizes
  if (containerWidth <= 0) {
    return currentSizes;
  }

  // Calculate total table width from all columns
  const currentTableWidth = columns.reduce(
    (sum, col) => sum + (currentSizes[col.id] ?? col.size ?? defaultColumnSize),
    0
  );

  // If table already fills or exceeds container, use current sizes
  if (currentTableWidth >= containerWidth) {
    return currentSizes;
  }

  const extraSpace = containerWidth - currentTableWidth;

  // Check if any column explicitly has growFirst set
  const growColumn = columns.find((col) => col.growFirst);

  if (growColumn) {
    // Grow only the designated column
    const currentGrowSize = currentSizes[growColumn.id] ?? growColumn.size ?? defaultColumnSize;
    const growColumnWidth = Math.max(
      growColumn.minSize ?? defaultColumnSize,
      currentGrowSize + extraSpace
    );

    return {
      ...currentSizes,
      [growColumn.id]: growColumnWidth,
    };
  }

  // No growFirst specified - distribute extra space equally across flexible columns
  // Columns with explicit sizes are fixed; only columns without explicit sizes grow
  const flexibleColumns = columns.filter((col) => !col.size && !currentSizes[col.id]);
  const fixedColumns = columns.filter((col) => col.size || currentSizes[col.id]);

  // If all columns have explicit sizes, no distribution needed
  if (flexibleColumns.length === 0) {
    return currentSizes;
  }

  const extraPerFlexColumn = extraSpace / flexibleColumns.length;
  const newSizes: ColumnSizingState = { ...currentSizes };

  // Fixed columns keep their exact size
  fixedColumns.forEach((col) => {
    newSizes[col.id] = currentSizes[col.id] ?? col.size!;
  });

  // Flexible columns get extra space distributed equally
  flexibleColumns.forEach((col) => {
    const baseSize = defaultColumnSize;
    const newSize = baseSize + extraPerFlexColumn;
    // Respect minSize constraint
    newSizes[col.id] = Math.max(col.minSize ?? defaultColumnSize, newSize);
  });

  return newSizes;
}
